import React, { useState } from 'react';
import { 
  ChevronRight, ChevronDown, Folder, Layout, Box, Database, Plug, Zap, Sparkles, 
  Send, Plus, Eye, Upload, Settings, GitBranch, Server, Cloud, Lock, User, LogOut, 
  Smartphone, Monitor, Tablet, Play, Code, Palette, Type, Image, Square, Circle, 
  Menu, X, Check, Search, Copy, Trash2, Edit3, Layers, Grid, Move, MousePointer, 
  Bell, Globe, Gift, Trophy, Star, CreditCard, MessageCircle, Phone, Wifi, MapPin, 
  QrCode, Shield, Users, BarChart3, TrendingUp, Headphones, Home, Package, History, 
  Gamepad2, Crown, Target, Megaphone, Languages, FileText, PanelLeft, Wallet, 
  Receipt, Banknote, Building2, PiggyBank, ArrowLeftRight, Landmark, DollarSign, 
  ShoppingCart, Store, Briefcase, LineChart, AlertCircle, CheckCircle, Clock, 
  RefreshCw, ChevronsUpDown 
} from 'lucide-react';

// ==================== PROJECT CONFIGURATIONS ====================

const projects = {
  selfcare: {
    id: 'selfcare',
    name: 'Selfcare',
    fullName: 'Telco Self-Care App',
    icon: '📱',
    color: '#0066cc',
    description: 'Customer self-service for telecom subscribers'
  },
  mobilemoney: {
    id: 'mobilemoney',
    name: 'MobileMoney',
    fullName: 'Mobile Money Platform',
    icon: '💰',
    color: '#10b981',
    description: 'Digital financial services platform'
  }
};

// ==================== SELFCARE APP CONFIGURATION ====================

const selfcareScreens = {
  onboarding: [
    { id: 'splash', name: 'Splash Screen', icon: '🚀', description: 'App launch & branding' },
    { id: 'onboarding-otp', name: 'OTP Verification', icon: '🔐', description: 'MSISDN & OTP authentication' },
    { id: 'ciam-login', name: 'CIAM Login', icon: '👤', description: 'Customer identity management' },
    { id: 'quick-tour', name: 'Quick Tour', icon: '📖', description: 'Feature walkthrough carousel' },
    { id: 'permissions', name: 'Permissions', icon: '✅', description: 'Location, notifications, contacts' },
  ],
  home: [
    { id: 'home-prepaid', name: 'Home (Prepaid)', icon: '💳', description: 'Prepaid account dashboard' },
    { id: 'home-postpaid', name: 'Home (Postpaid)', icon: '📄', description: 'Postpaid account dashboard' },
    { id: 'linked-accounts', name: 'Linked Accounts', icon: '🔗', description: 'Multi-number management' },
    { id: 'account-switcher', name: 'Account Switcher', icon: '🔄', description: 'Switch between accounts' },
  ],
  bundles: [
    { id: 'bundles-list', name: 'Bundles', icon: '📦', description: 'Browse all bundles' },
    { id: 'create-bundle', name: 'Create Your Bundle', icon: '🎨', description: 'Custom bundle builder' },
    { id: 'esim', name: 'eSIM', icon: '📲', description: 'eSIM activation & management' },
    { id: 'roaming', name: 'International Roaming', icon: '🌍', description: 'Roaming packages' },
    { id: 'caller-tunes', name: 'Caller Tunes', icon: '🎵', description: 'Ringback tones' },
  ],
  recharge: [
    { id: 'recharge', name: 'Recharge', icon: '💰', description: 'Top-up main balance' },
    { id: 'qr-voucher', name: 'QR & Voucher Top-up', icon: '📷', description: 'Scan QR or enter voucher' },
    { id: 'transactions', name: 'Historic Transactions', icon: '📋', description: 'Transaction history' },
  ],
  payments: [
    { id: 'payment-methods', name: 'Payment Methods', icon: '💳', description: 'Manage payment options' },
    { id: 'digital-wallet', name: 'Digital Wallet', icon: '👛', description: 'Wallet balance & top-up' },
    { id: 'cards', name: 'Cards', icon: '💳', description: 'Credit/Debit cards' },
    { id: 'net-banking', name: 'Net Banking', icon: '🏦', description: 'Bank transfers' },
    { id: 'pay-by-loyalty', name: 'Pay by Loyalty', icon: '⭐', description: 'Redeem points for payment' },
  ],
  gamification: [
    { id: 'daily-challenges', name: 'Daily Challenges', icon: '🎯', description: 'Complete tasks for rewards' },
    { id: 'leaderboard', name: 'Leaderboard', icon: '🏆', description: 'Rankings & competitions' },
    { id: 'rewards', name: 'Gamification Rewards', icon: '🎁', description: 'Claim earned rewards' },
  ],
  loyalty: [
    { id: 'loyalty-home', name: 'Loyalty Home', icon: '⭐', description: 'Points & tier status' },
    { id: 'offer-network', name: 'Offer Network', icon: '🤝', description: 'Partner offers' },
    { id: 'redeem', name: 'Redeem Points', icon: '🎁', description: 'Rewards catalog' },
  ],
  notifications: [
    { id: 'notifications', name: 'Notifications', icon: '🔔', description: 'All notifications' },
    { id: 'in-app', name: 'In-App Messages', icon: '💬', description: 'Promotional messages' },
    { id: 'actionable', name: 'Actionable Alerts', icon: '⚡', description: 'Quick action notifications' },
  ],
  support: [
    { id: 'help', name: 'Help Center', icon: '❓', description: 'FAQ & guides' },
    { id: 'live-chat', name: 'Live Chat', icon: '💬', description: 'AI & agent chat' },
    { id: 'store-locator', name: 'Store Locator', icon: '📍', description: 'Find nearby stores' },
  ],
  promotions: [
    { id: 'offers', name: 'Promotions & Offers', icon: '🎉', description: 'Current promotions' },
    { id: 'banners', name: 'Banners', icon: '🖼️', description: 'Featured content' },
    { id: 'personalized', name: 'For You', icon: '✨', description: 'Personalized offers' },
  ],
  settings: [
    { id: 'menu', name: 'Menu', icon: '☰', description: 'Main navigation' },
    { id: 'profile', name: 'Profile', icon: '👤', description: 'Account settings' },
    { id: 'language', name: 'Language', icon: '🌐', description: 'Multilingual support' },
    { id: 'search', name: 'Search', icon: '🔍', description: 'Global search' },
  ],
};

// ==================== MOBILE MONEY APP CONFIGURATION ====================

const mobileMoneyScreens = {
  onboarding: [
    { id: 'mm-splash', name: 'Splash Screen', icon: '💰', description: 'App launch & branding' },
    { id: 'mm-registration', name: 'Registration', icon: '📝', description: 'New user registration' },
    { id: 'mm-kyc', name: 'KYC Verification', icon: '🪪', description: 'Identity verification' },
    { id: 'mm-otp', name: 'OTP Verification', icon: '🔐', description: 'Phone number verification' },
    { id: 'mm-pin-setup', name: 'PIN Setup', icon: '🔑', description: 'Create transaction PIN' },
    { id: 'mm-biometric', name: 'Biometric Setup', icon: '👆', description: 'Fingerprint/Face ID setup' },
  ],
  wallet: [
    { id: 'mm-home', name: 'Wallet Home', icon: '🏠', description: 'Main dashboard & balance' },
    { id: 'mm-balance', name: 'Balance Details', icon: '💵', description: 'Available & pending balance' },
    { id: 'mm-add-money', name: 'Add Money', icon: '➕', description: 'Fund wallet from bank/card' },
    { id: 'mm-withdraw', name: 'Withdraw', icon: '🏧', description: 'Cash out to bank/ATM' },
    { id: 'mm-wallet-history', name: 'Wallet History', icon: '📜', description: 'All wallet transactions' },
  ],
  transfers: [
    { id: 'mm-send-money', name: 'Send Money', icon: '📤', description: 'P2P money transfer' },
    { id: 'mm-request-money', name: 'Request Money', icon: '📥', description: 'Request payment from others' },
    { id: 'mm-contacts', name: 'Contacts', icon: '👥', description: 'Frequent recipients' },
    { id: 'mm-transfer-history', name: 'Transfer History', icon: '📋', description: 'Past transfers' },
    { id: 'mm-scheduled', name: 'Scheduled Transfers', icon: '📅', description: 'Recurring payments' },
  ],
  billpay: [
    { id: 'mm-bills-home', name: 'Bill Payments', icon: '📄', description: 'Pay all your bills' },
    { id: 'mm-utilities', name: 'Utilities', icon: '💡', description: 'Electricity, water, gas' },
    { id: 'mm-school-fees', name: 'School Fees', icon: '🎓', description: 'Education payments' },
    { id: 'mm-subscriptions', name: 'Subscriptions', icon: '📺', description: 'TV, streaming, internet' },
    { id: 'mm-government', name: 'Government', icon: '🏛️', description: 'Taxes, licenses, permits' },
    { id: 'mm-bill-reminders', name: 'Bill Reminders', icon: '⏰', description: 'Payment reminders' },
    { id: 'mm-autopay', name: 'Auto Pay Setup', icon: '🔄', description: 'Automatic bill payments' },
  ],
  airtime: [
    { id: 'mm-airtime-self', name: 'Buy Airtime', icon: '📱', description: 'Recharge own number' },
    { id: 'mm-airtime-others', name: 'Airtime for Others', icon: '🎁', description: 'Send airtime to contacts' },
    { id: 'mm-data-bundles', name: 'Data Bundles', icon: '📶', description: 'Mobile data packages' },
    { id: 'mm-airtime-history', name: 'Airtime History', icon: '📋', description: 'Top-up history' },
  ],
  merchant: [
    { id: 'mm-scan-pay', name: 'Scan & Pay', icon: '📷', description: 'QR code payments' },
    { id: 'mm-tap-pay', name: 'Tap to Pay', icon: '📶', description: 'NFC contactless payment' },
    { id: 'mm-merchant-search', name: 'Find Merchants', icon: '🔍', description: 'Nearby accepting merchants' },
    { id: 'mm-payment-receipt', name: 'Payment Receipt', icon: '🧾', description: 'Digital receipts' },
    { id: 'mm-split-bill', name: 'Split Bill', icon: '✂️', description: 'Share payments with friends' },
  ],
  financial: [
    { id: 'mm-savings', name: 'Savings', icon: '🐷', description: 'Savings goals & accounts' },
    { id: 'mm-loans', name: 'Micro Loans', icon: '💳', description: 'Quick loans & repayment' },
    { id: 'mm-insurance', name: 'Micro Insurance', icon: '🛡️', description: 'Insurance products' },
    { id: 'mm-investments', name: 'Investments', icon: '📈', description: 'Investment options' },
    { id: 'mm-credit-score', name: 'Credit Score', icon: '📊', description: 'Credit rating & history' },
  ],
  remittance: [
    { id: 'mm-send-intl', name: 'Send International', icon: '🌍', description: 'Cross-border transfers' },
    { id: 'mm-receive-intl', name: 'Receive International', icon: '📥', description: 'Incoming remittances' },
    { id: 'mm-exchange-rates', name: 'Exchange Rates', icon: '💱', description: 'Currency conversion' },
    { id: 'mm-remittance-history', name: 'Remittance History', icon: '📋', description: 'International transfers' },
    { id: 'mm-beneficiaries', name: 'Beneficiaries', icon: '👥', description: 'Saved recipients abroad' },
  ],
  atm: [
    { id: 'mm-cardless-withdraw', name: 'Cardless Withdrawal', icon: '🏧', description: 'ATM without card' },
    { id: 'mm-generate-code', name: 'Generate Code', icon: '🔢', description: 'One-time withdrawal code' },
    { id: 'mm-atm-locator', name: 'ATM Locator', icon: '📍', description: 'Find nearby ATMs' },
    { id: 'mm-virtual-card', name: 'Virtual Card', icon: '💳', description: 'Digital debit card' },
    { id: 'mm-companion-card', name: 'Companion Card', icon: '💳', description: 'Physical card management' },
  ],
  insights: [
    { id: 'mm-dashboard', name: 'Financial Dashboard', icon: '📊', description: 'Overview & analytics' },
    { id: 'mm-spending', name: 'Spending Analysis', icon: '📉', description: 'Track expenses by category' },
    { id: 'mm-budgets', name: 'Budgets', icon: '🎯', description: 'Set & track budgets' },
    { id: 'mm-alerts', name: 'Smart Alerts', icon: '🔔', description: 'Spending alerts & tips' },
    { id: 'mm-reports', name: 'Reports', icon: '📑', description: 'Download statements' },
  ],
  rewards: [
    { id: 'mm-cashback', name: 'Cashback', icon: '💸', description: 'Earn cashback rewards' },
    { id: 'mm-promotions', name: 'Promotions', icon: '🎉', description: 'Current offers' },
    { id: 'mm-referral', name: 'Refer & Earn', icon: '👥', description: 'Invite friends for rewards' },
    { id: 'mm-loyalty', name: 'Loyalty Points', icon: '⭐', description: 'Points & redemption' },
  ],
  support: [
    { id: 'mm-help', name: 'Help Center', icon: '❓', description: 'FAQ & guides' },
    { id: 'mm-chat', name: 'Live Chat', icon: '💬', description: 'Customer support' },
    { id: 'mm-agent-locator', name: 'Agent Locator', icon: '📍', description: 'Find cash-in/cash-out agents' },
    { id: 'mm-dispute', name: 'Raise Dispute', icon: '⚠️', description: 'Transaction disputes' },
  ],
  settings: [
    { id: 'mm-profile', name: 'Profile', icon: '👤', description: 'Account details' },
    { id: 'mm-security', name: 'Security', icon: '🔒', description: 'PIN, biometrics, 2FA' },
    { id: 'mm-limits', name: 'Transaction Limits', icon: '📏', description: 'View & request limits' },
    { id: 'mm-linked-accounts', name: 'Linked Accounts', icon: '🔗', description: 'Banks & cards' },
    { id: 'mm-notifications', name: 'Notifications', icon: '🔔', description: 'Alert preferences' },
  ],
};

// Mobile Money CMS Content Types
const mobileMoneyContentTypes = [
  { id: 'mm-banners', name: 'Home Banners', icon: '🖼️', count: 8, status: 'active' },
  { id: 'mm-promotions', name: 'Promotions', icon: '🎉', count: 15, status: 'active' },
  { id: 'mm-billers', name: 'Biller Catalog', icon: '📄', count: 250, status: 'active' },
  { id: 'mm-merchants', name: 'Merchant Directory', icon: '🏪', count: 5000, status: 'active' },
  { id: 'mm-exchange', name: 'Exchange Rates', icon: '💱', count: 45, status: 'active' },
  { id: 'mm-campaigns', name: 'Push Campaigns', icon: '📢', count: 5, status: 'scheduled' },
];

// Mobile Money SDK Integrations
const mobileMoneySDKs = [
  { id: 'mm-firebase', name: 'Firebase Analytics', icon: '🔥', status: 'connected', events: '120K/day' },
  { id: 'mm-crashlytics', name: 'Crashlytics', icon: '🐛', status: 'connected', crashes: '0.05%' },
  { id: 'mm-fraud', name: 'Fraud Detection AI', icon: '🛡️', status: 'connected', blocked: '99.9%' },
  { id: 'mm-kyc', name: 'KYC Provider', icon: '🪪', status: 'connected', verified: '98%' },
  { id: 'mm-maps', name: 'Google Maps', icon: '🗺️', status: 'connected', calls: '25K/day' },
  { id: 'mm-biometric', name: 'Biometric SDK', icon: '👆', status: 'connected', auth: '99.5%' },
];

// Mobile Money Data Connectors
const mobileMoneyConnectors = [
  { id: 1, name: 'Core Banking', icon: '🏦', status: 'connected', type: 'core', version: 'v2.0' },
  { id: 2, name: 'Payment Switch', icon: '🔀', status: 'connected', type: 'payments', version: 'v3.1' },
  { id: 3, name: 'Card Processor', icon: '💳', status: 'connected', type: 'cards', version: 'v2.5' },
  { id: 4, name: 'Bill Aggregator', icon: '📄', status: 'connected', type: 'bills', version: 'v1.8' },
  { id: 5, name: 'Remittance Hub', icon: '🌍', status: 'connected', type: 'remittance', version: 'v2.0' },
  { id: 6, name: 'KYC/AML Engine', icon: '🛡️', status: 'connected', type: 'compliance', version: 'v3.0' },
  { id: 7, name: 'Fraud Engine', icon: '🚨', status: 'connected', type: 'security', version: 'v4.0' },
  { id: 8, name: 'Notification Hub', icon: '🔔', status: 'connected', type: 'messaging', version: 'v2.2' },
  { id: 9, name: 'Savings Engine', icon: '🐷', status: 'connected', type: 'savings', version: 'v1.5' },
  { id: 10, name: 'Loan Management', icon: '💰', status: 'connected', type: 'lending', version: 'v2.0' },
  { id: 11, name: 'Insurance Gateway', icon: '🛡️', status: 'connected', type: 'insurance', version: 'v1.2' },
  { id: 12, name: 'ATM Network', icon: '🏧', status: 'connected', type: 'atm', version: 'v1.8' },
  { id: 13, name: 'QR Code Engine', icon: '📱', status: 'connected', type: 'merchant', version: 'v2.0' },
  { id: 14, name: 'Analytics Platform', icon: '📊', status: 'connected', type: 'analytics', version: 'v3.0' },
  { id: 15, name: 'CMS Headless', icon: '📝', status: 'connected', type: 'content', version: 'v2.1' },
];

// Mobile Money Design Tokens
const mobileMoneyDesignTokens = {
  colors: [
    { name: 'MoMo Primary', value: '#10b981', category: 'brand' },
    { name: 'MoMo Dark', value: '#059669', category: 'brand' },
    { name: 'Accent Gold', value: '#f59e0b', category: 'brand' },
    { name: 'Send Green', value: '#22c55e', category: 'transaction' },
    { name: 'Receive Blue', value: '#3b82f6', category: 'transaction' },
    { name: 'Bill Orange', value: '#f97316', category: 'transaction' },
    { name: 'Success', value: '#22c55e', category: 'semantic' },
    { name: 'Warning', value: '#f59e0b', category: 'semantic' },
    { name: 'Error', value: '#ef4444', category: 'semantic' },
  ],
  components: [
    { name: 'Balance Card', icon: Wallet, category: 'wallet' },
    { name: 'Transaction Row', icon: ArrowLeftRight, category: 'wallet' },
    { name: 'Quick Send', icon: Send, category: 'transfer' },
    { name: 'Contact Avatar', icon: User, category: 'transfer' },
    { name: 'Biller Card', icon: Receipt, category: 'bills' },
    { name: 'Amount Input', icon: DollarSign, category: 'input' },
    { name: 'PIN Pad', icon: Shield, category: 'security' },
    { name: 'QR Scanner', icon: QrCode, category: 'merchant' },
    { name: 'Savings Goal', icon: PiggyBank, category: 'financial' },
    { name: 'Loan Card', icon: Banknote, category: 'financial' },
    { name: 'Exchange Rate', icon: RefreshCw, category: 'remittance' },
    { name: 'ATM Marker', icon: MapPin, category: 'atm' },
    { name: 'Budget Gauge', icon: Target, category: 'insights' },
    { name: 'Alert Card', icon: AlertCircle, category: 'notifications' },
  ]
};

// Selfcare CMS Content Types
const selfcareContentTypes = [
  { id: 'banners', name: 'Banners', icon: '🖼️', count: 12, status: 'active' },
  { id: 'promotions', name: 'Promotions', icon: '🎉', count: 8, status: 'active' },
  { id: 'offers', name: 'Offers', icon: '🎁', count: 24, status: 'active' },
  { id: 'loyalty-rewards', name: 'Loyalty Rewards', icon: '⭐', count: 15, status: 'active' },
  { id: 'gamification', name: 'Gamification', icon: '🎮', count: 6, status: 'active' },
  { id: 'notifications', name: 'Push Campaigns', icon: '📢', count: 3, status: 'scheduled' },
];

// Selfcare SDK Integrations
const selfcareSDKs = [
  { id: 'firebase', name: 'Firebase Analytics', icon: '🔥', status: 'connected', events: '45K/day' },
  { id: 'crashlytics', name: 'Crashlytics SDK', icon: '🐛', status: 'connected', crashes: '0.1%' },
  { id: 'fcm', name: 'FCM Push', icon: '📬', status: 'connected', delivered: '98.5%' },
  { id: 'urban-airship', name: 'Urban Airship', icon: '✈️', status: 'available', delivered: '-' },
  { id: 'google-maps', name: 'Google Maps', icon: '🗺️', status: 'connected', calls: '12K/day' },
  { id: 'digital-kpis', name: 'Digital KPIs', icon: '📊', status: 'connected', metrics: '28' },
];

// Selfcare Data Connectors
const selfcareConnectors = [
  { id: 1, name: 'TMF629 Customer', icon: '👤', status: 'connected', type: 'tmforum', version: 'v4.0' },
  { id: 2, name: 'TMF632 Party', icon: '👥', status: 'connected', type: 'tmforum', version: 'v4.0' },
  { id: 3, name: 'TMF637 Product', icon: '📦', status: 'connected', type: 'tmforum', version: 'v4.0' },
  { id: 4, name: 'TMF666 Account', icon: '💰', status: 'connected', type: 'tmforum', version: 'v4.0' },
  { id: 5, name: 'TMF678 Usage', icon: '📊', status: 'connected', type: 'tmforum', version: 'v4.0' },
  { id: 6, name: 'TMF676 Payment', icon: '💳', status: 'connected', type: 'tmforum', version: 'v4.0' },
  { id: 7, name: 'BSS Billing', icon: '🧾', status: 'connected', type: 'oss-bss' },
  { id: 8, name: 'OSS Inventory', icon: '📋', status: 'connected', type: 'oss-bss' },
  { id: 9, name: 'CRM Siebel', icon: '🎯', status: 'connected', type: 'crm' },
  { id: 10, name: 'CIAM Auth', icon: '🔐', status: 'connected', type: 'identity' },
  { id: 11, name: 'Loyalty Engine', icon: '⭐', status: 'connected', type: 'engagement' },
  { id: 12, name: 'Gamification API', icon: '🎮', status: 'connected', type: 'engagement' },
  { id: 13, name: 'CMS Headless', icon: '📝', status: 'connected', type: 'content' },
  { id: 14, name: 'FCM/APNS', icon: '🔔', status: 'connected', type: 'notifications' },
  { id: 15, name: 'Payment Gateway', icon: '💳', status: 'connected', type: 'payments' },
];

// Selfcare Design Tokens
const selfcareDesignTokens = {
  colors: [
    { name: 'Telco Primary', value: '#0066cc', category: 'brand' },
    { name: 'Telco Dark', value: '#004d99', category: 'brand' },
    { name: 'Accent Gold', value: '#ffcc00', category: 'brand' },
    { name: 'Prepaid Green', value: '#10b981', category: 'product' },
    { name: 'Postpaid Blue', value: '#3b82f6', category: 'product' },
    { name: 'Success', value: '#22c55e', category: 'semantic' },
    { name: 'Warning', value: '#f59e0b', category: 'semantic' },
    { name: 'Error', value: '#ef4444', category: 'semantic' },
  ],
  components: [
    { name: 'Balance Card', icon: CreditCard, category: 'telco' },
    { name: 'Usage Gauge', icon: Circle, category: 'telco' },
    { name: 'Bundle Card', icon: Package, category: 'telco' },
    { name: 'Quick Action', icon: Zap, category: 'telco' },
    { name: 'Offer Banner', icon: Megaphone, category: 'telco' },
    { name: 'Transaction Row', icon: History, category: 'telco' },
    { name: 'Loyalty Tier', icon: Crown, category: 'gamification' },
    { name: 'Challenge Card', icon: Target, category: 'gamification' },
    { name: 'Leaderboard Row', icon: Trophy, category: 'gamification' },
    { name: 'Reward Card', icon: Gift, category: 'gamification' },
    { name: 'OTP Input', icon: Shield, category: 'auth' },
    { name: 'QR Scanner', icon: QrCode, category: 'input' },
    { name: 'Map View', icon: MapPin, category: 'maps' },
    { name: 'Chat Bubble', icon: MessageCircle, category: 'support' },
  ]
};

// SSO Providers (shared)
const ssoProviders = [
  { id: 'google', name: 'Google Workspace', icon: '🔵', color: '#4285f4' },
  { id: 'microsoft', name: 'Microsoft Entra', icon: '🟦', color: '#00a4ef' },
  { id: 'okta', name: 'Okta', icon: '🔷', color: '#007dc1' },
  { id: 'ping', name: 'PingIdentity', icon: '🔴', color: '#b91c1c' },
];

// Infrastructure Options (shared)
const infrastructureOptions = [
  { id: 'aws', name: 'AWS', icon: '☁️', regions: ['us-east-1', 'eu-west-1', 'ap-south-1', 'me-south-1'] },
  { id: 'azure', name: 'Azure', icon: '🔷', regions: ['eastus', 'westeurope', 'centralindia', 'uaenorth'] },
  { id: 'gcp', name: 'GCP', icon: '🔵', regions: ['us-central1', 'europe-west1', 'asia-south1'] },
  { id: 'private', name: 'Private Cloud', icon: '🏢', regions: ['dc-primary', 'dc-secondary'] },
];

// AI Prompts for each project
const selfcareAIPrompts = [
  'Add OTP verification with resend timer',
  'Create prepaid balance card with recharge button',
  'Build data usage gauge with app breakdown',
  'Add gamification daily challenges',
  'Create loyalty points redemption catalog',
  'Build international roaming package selector',
  'Add eSIM activation with QR scanner',
  'Create AI-powered live chat support',
  'Build promotional banner carousel',
  'Add family plan member management',
];

const mobileMoneyAIPrompts = [
  'Create wallet balance card with add money button',
  'Build P2P send money flow with contacts',
  'Add QR code scanner for merchant payments',
  'Create bill payment with biller search',
  'Build savings goal tracker with progress',
  'Add micro-loan application flow',
  'Create international remittance with exchange rates',
  'Build cardless ATM withdrawal flow',
  'Add spending insights with category breakdown',
  'Create budget setup with alerts',
];

// ==================== SCREEN PREVIEW COMPONENT ====================

const ScreenPreview = ({ screen, project, projectColor }) => {
  // Selfcare Home Prepaid
  if (screen.id === 'home-prepaid') {
    return (
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>Good Morning</p>
            <h2 style={{ color: 'white', fontWeight: '600', margin: 0 }}>Ahmed Hassan</h2>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bell size={16} color="#9ca3af" />
            </div>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Search size={16} color="#9ca3af" />
            </div>
          </div>
        </div>
        
        <div style={{ borderRadius: '16px', padding: '16px', background: `linear-gradient(135deg, ${projectColor}, ${projectColor}aa)` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Main Balance</p>
              <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: 'white', margin: '4px 0' }}>AED 125.50</h1>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', margin: 0 }}>Expires: 15 Jan 2026</p>
            </div>
            <span style={{ padding: '4px 8px', background: 'rgba(255,255,255,0.2)', borderRadius: '20px', fontSize: '12px', color: 'white' }}>Prepaid</span>
          </div>
          <button style={{ width: '100%', padding: '10px', background: 'white', borderRadius: '12px', border: 'none', fontWeight: '500', color: projectColor, cursor: 'pointer' }}>
            Recharge Now
          </button>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {[
            { label: 'Data', used: '5.7GB', total: '10GB', percent: 57, color: '#3b82f6' },
            { label: 'Voice', used: '128', total: '200', percent: 64, color: '#10b981' },
            { label: 'SMS', used: '45', total: '100', percent: 45, color: '#f59e0b' },
          ].map(usage => (
            <div key={usage.label} style={{ background: 'rgba(51,65,85,0.5)', borderRadius: '12px', padding: '12px', textAlign: 'center' }}>
              <div style={{ position: 'relative', width: '48px', height: '48px', margin: '0 auto 8px' }}>
                <svg width="48" height="48" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="24" cy="24" r="20" stroke="#1e293b" strokeWidth="4" fill="none" />
                  <circle cx="24" cy="24" r="20" stroke={usage.color} strokeWidth="4" fill="none"
                    strokeDasharray={`${usage.percent * 1.26} 126`} />
                </svg>
                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold', color: 'white' }}>{usage.percent}%</span>
              </div>
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>{usage.label}</p>
              <p style={{ fontSize: '12px', color: 'white', margin: '2px 0 0' }}>{usage.used}/{usage.total}</p>
            </div>
          ))}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
          {['Recharge', 'Bundles', 'Roaming', 'Offers'].map((action, i) => (
            <button key={action} style={{ padding: '12px', background: 'rgba(51,65,85,0.5)', borderRadius: '12px', border: 'none', textAlign: 'center', cursor: 'pointer' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', margin: '0 auto 4px', background: `${projectColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {[<CreditCard size={16} color={projectColor} />, <Package size={16} color={projectColor} />, <Globe size={16} color={projectColor} />, <Gift size={16} color={projectColor} />][i]}
              </div>
              <span style={{ fontSize: '12px', color: '#d1d5db' }}>{action}</span>
            </button>
          ))}
        </div>
        
        <div style={{ background: 'linear-gradient(90deg, rgba(234,179,8,0.2), rgba(249,115,22,0.2))', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Trophy size={24} color="#eab308" />
          <div style={{ flex: 1 }}>
            <p style={{ color: 'white', fontWeight: '500', margin: 0 }}>Gold Member</p>
            <p style={{ fontSize: '14px', color: '#9ca3af', margin: 0 }}>2,450 points</p>
          </div>
          <button style={{ padding: '4px 12px', background: 'rgba(234,179,8,0.3)', borderRadius: '8px', border: 'none', color: '#fbbf24', fontSize: '14px', cursor: 'pointer' }}>Redeem</button>
        </div>
      </div>
    );
  }
  
  // Mobile Money Home
  if (screen.id === 'mm-home') {
    return (
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>Welcome back</p>
            <h2 style={{ color: 'white', fontWeight: '600', margin: 0 }}>John Kamau</h2>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bell size={16} color="#9ca3af" />
            </div>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Settings size={16} color="#9ca3af" />
            </div>
          </div>
        </div>
        
        <div style={{ borderRadius: '16px', padding: '16px', background: `linear-gradient(135deg, ${projectColor}, ${projectColor}aa)` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                Available Balance <Eye size={14} />
              </p>
              <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: 'white', margin: '4px 0' }}>KES 45,230.00</h1>
            </div>
            <Wallet size={24} color="rgba(255,255,255,0.5)" />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ flex: 1, padding: '10px', background: 'white', borderRadius: '12px', border: 'none', fontWeight: '500', color: projectColor, cursor: 'pointer' }}>
              Add Money
            </button>
            <button style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.2)', borderRadius: '12px', border: 'none', fontWeight: '500', color: 'white', cursor: 'pointer' }}>
              Withdraw
            </button>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
          {[
            { icon: <Send size={18} />, label: 'Send', color: '#22c55e' },
            { icon: <QrCode size={18} />, label: 'Scan Pay', color: '#3b82f6' },
            { icon: <Receipt size={18} />, label: 'Bills', color: '#f97316' },
            { icon: <Phone size={18} />, label: 'Airtime', color: '#8b5cf6' },
          ].map((action, i) => (
            <button key={action.label} style={{ padding: '12px', background: 'rgba(51,65,85,0.5)', borderRadius: '12px', border: 'none', textAlign: 'center', cursor: 'pointer' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', margin: '0 auto 4px', background: `${action.color}20`, color: action.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {action.icon}
              </div>
              <span style={{ fontSize: '12px', color: '#d1d5db' }}>{action.label}</span>
            </button>
          ))}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
          <div style={{ background: 'rgba(51,65,85,0.5)', borderRadius: '12px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <PiggyBank size={18} color="#ec4899" />
              <span style={{ fontSize: '14px', color: '#d1d5db' }}>Savings</span>
            </div>
            <p style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', margin: 0 }}>KES 12,500</p>
            <p style={{ fontSize: '12px', color: '#34d399', margin: '4px 0 0' }}>+5.2% APY</p>
          </div>
          <div style={{ background: 'rgba(51,65,85,0.5)', borderRadius: '12px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Banknote size={18} color="#60a5fa" />
              <span style={{ fontSize: '14px', color: '#d1d5db' }}>Loan Limit</span>
            </div>
            <p style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', margin: 0 }}>KES 50,000</p>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0' }}>Pre-approved</p>
          </div>
        </div>
        
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ color: 'white', fontWeight: '500', margin: 0 }}>Recent Transactions</h3>
            <button style={{ fontSize: '14px', color: projectColor, background: 'none', border: 'none', cursor: 'pointer' }}>See All</button>
          </div>
          {[
            { name: 'Mary Wanjiku', type: 'Sent', amount: '-KES 2,500', time: '2 min ago', color: '#ef4444' },
            { name: 'KPLC Electricity', type: 'Bill', amount: '-KES 3,200', time: '1 hr ago', color: '#f97316' },
            { name: 'Peter Ochieng', type: 'Received', amount: '+KES 5,000', time: '3 hrs ago', color: '#22c55e' },
          ].map((tx, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(51,65,85,0.5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ArrowLeftRight size={16} color={tx.color} />
                </div>
                <div>
                  <p style={{ color: 'white', fontSize: '14px', margin: 0 }}>{tx.name}</p>
                  <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>{tx.time}</p>
                </div>
              </div>
              <span style={{ fontWeight: '500', color: tx.amount.startsWith('+') ? '#34d399' : 'white' }}>{tx.amount}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Default Preview
  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <span style={{ fontSize: '32px' }}>{screen.icon}</span>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'white', margin: 0 }}>{screen.name}</h2>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>{screen.description}</p>
        </div>
      </div>
      
      <div style={{ background: 'rgba(51,65,85,0.5)', borderRadius: '12px', padding: '24px', textAlign: 'center' }}>
        <Layout size={48} color="#475569" style={{ margin: '0 auto 12px' }} />
        <p style={{ color: '#9ca3af', margin: 0 }}>Screen preview will appear here</p>
        <p style={{ fontSize: '14px', color: '#64748b', margin: '8px 0 0' }}>Use AI Build or Manual tab to design this screen</p>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ height: '80px', background: 'rgba(51,65,85,0.3)', borderRadius: '12px' }} />
        ))}
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const CXBuilderMultiProject = () => {
  const [view, setView] = useState('login');
  const [currentProject, setCurrentProject] = useState('selfcare');
  const [showProjectSwitcher, setShowProjectSwitcher] = useState(false);
  
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['onboarding', 'home', 'wallet', 'transfers']);
  const [leftTab, setLeftTab] = useState('screens');
  const [rightTab, setRightTab] = useState('ai');
  const [devicePreview, setDevicePreview] = useState('mobile');
  const [showPublishModal, setShowPublishModal] = useState(false);
  
  const [infrastructure, setInfrastructure] = useState('aws');
  const [region, setRegion] = useState('us-east-1');
  const [gitRepo, setGitRepo] = useState('https://github.com/telco/app.git');
  const [gitBranch, setGitBranch] = useState('main');
  
  const project = projects[currentProject];
  const screens = currentProject === 'selfcare' ? selfcareScreens : mobileMoneyScreens;
  const contentTypes = currentProject === 'selfcare' ? selfcareContentTypes : mobileMoneyContentTypes;
  const sdkIntegrations = currentProject === 'selfcare' ? selfcareSDKs : mobileMoneySDKs;
  const dataConnectors = currentProject === 'selfcare' ? selfcareConnectors : mobileMoneyConnectors;
  const designTokens = currentProject === 'selfcare' ? selfcareDesignTokens : mobileMoneyDesignTokens;
  const aiPrompts = currentProject === 'selfcare' ? selfcareAIPrompts : mobileMoneyAIPrompts;
  
  const categoryLabels = currentProject === 'selfcare' ? {
    onboarding: 'Onboarding',
    home: 'Home',
    bundles: 'Bundles',
    recharge: 'Recharge',
    payments: 'Payments',
    gamification: 'Gamification',
    loyalty: 'Loyalty',
    notifications: 'Notifications',
    support: 'Support',
    promotions: 'Promotions',
    settings: 'Settings',
  } : {
    onboarding: 'Onboarding',
    wallet: 'Digital Wallet',
    transfers: 'P2P Transfers',
    billpay: 'Bill Payments',
    airtime: 'Airtime & Data',
    merchant: 'Merchant Pay',
    financial: 'Financial Services',
    remittance: 'Remittances',
    atm: 'ATM & Cards',
    insights: 'Financial Insights',
    rewards: 'Rewards',
    support: 'Support',
    settings: 'Settings',
  };
  
  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  // Styles
  const styles = {
    container: { height: '100vh', background: '#0f172a', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    header: { height: '56px', background: 'rgba(30,41,59,0.9)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(51,65,85,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', flexShrink: 0 },
    main: { flex: 1, display: 'flex', overflow: 'hidden' },
    leftPanel: { width: '320px', background: 'rgba(30,41,59,0.5)', borderRight: '1px solid rgba(51,65,85,0.5)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
    centerPanel: { flex: 1, display: 'flex', flexDirection: 'column', background: 'rgba(15,23,42,0.5)', overflow: 'hidden' },
    rightPanel: { width: '384px', background: 'rgba(30,41,59,0.5)', borderLeft: '1px solid rgba(51,65,85,0.5)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
    tabBar: { display: 'flex', borderBottom: '1px solid rgba(51,65,85,0.5)' },
    tab: (active, color) => ({ flex: 1, padding: '12px 0', fontSize: '12px', fontWeight: '500', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', color: active ? 'white' : '#64748b', borderBottom: active ? `2px solid ${color || '#7c3aed'}` : '2px solid transparent', background: 'none', border: 'none' }),
    button: (bg, color) => ({ padding: '8px 16px', borderRadius: '8px', background: bg, color: color || 'white', border: 'none', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }),
  };

  // LOGIN VIEW
  if (view === 'login') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a, #1e1b4b, #0f172a)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' }}>
        <div style={{ position: 'relative', background: 'rgba(30,41,59,0.8)', backdropFilter: 'blur(24px)', borderRadius: '24px', padding: '40px', width: '100%', maxWidth: '420px', border: '1px solid rgba(51,65,85,0.5)' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '16px', background: 'linear-gradient(135deg, #3b82f6, #10b981)', marginBottom: '16px' }}>
              <Layers size={32} color="white" />
            </div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: 'white', margin: '0 0 8px' }}>CX Builder</h1>
            <p style={{ color: '#9ca3af', margin: 0 }}>No-Code App Platform</p>
          </div>
          
          <div style={{ marginBottom: '32px' }}>
            <p style={{ fontSize: '14px', color: '#64748b', textAlign: 'center', marginBottom: '16px' }}>Sign in with Enterprise SSO</p>
            {ssoProviders.map(provider => (
              <button
                key={provider.id}
                onClick={() => setView('setup')}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '12px 16px', borderRadius: '12px', background: 'rgba(51,65,85,0.5)', border: '1px solid rgba(71,85,105,0.5)', color: 'white', marginBottom: '12px', cursor: 'pointer', transition: 'all 0.2s' }}
              >
                <span style={{ fontSize: '20px' }}>{provider.icon}</span>
                <span>Continue with {provider.name}</span>
              </button>
            ))}
          </div>
          
          <p style={{ fontSize: '12px', color: '#64748b', textAlign: 'center', margin: 0 }}>
            By signing in, you agree to our Terms of Service
          </p>
        </div>
      </div>
    );
  }

  // SETUP VIEW
  if (view === 'setup') {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a, #1e1b4b, #0f172a)', padding: '32px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
            <div style={{ padding: '12px', borderRadius: '12px', background: 'linear-gradient(135deg, #3b82f6, #10b981)' }}>
              <Settings size={24} color="white" />
            </div>
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'white', margin: 0 }}>Project Setup</h1>
              <p style={{ color: '#9ca3af', margin: 0 }}>Configure your app infrastructure</p>
            </div>
          </div>
          
          {/* Project Selection */}
          <div style={{ background: 'rgba(30,41,59,0.5)', backdropFilter: 'blur(8px)', borderRadius: '16px', padding: '24px', border: '1px solid rgba(51,65,85,0.5)', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'white', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Folder size={20} color="#a855f7" />
              Select Project Type
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              {Object.values(projects).map(p => (
                <button
                  key={p.id}
                  onClick={() => setCurrentProject(p.id)}
                  style={{
                    padding: '24px',
                    borderRadius: '12px',
                    border: `2px solid ${currentProject === p.id ? '#10b981' : '#475569'}`,
                    background: currentProject === p.id ? 'rgba(16,185,129,0.1)' : 'rgba(51,65,85,0.3)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>{p.icon}</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: 'white' }}>{p.fullName}</div>
                  <div style={{ fontSize: '14px', color: '#9ca3af', marginTop: '4px' }}>{p.description}</div>
                  {currentProject === p.id && (
                    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '4px', color: '#34d399', fontSize: '14px' }}>
                      <Check size={14} /> Selected
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
          
          {/* Infrastructure Selection */}
          <div style={{ background: 'rgba(30,41,59,0.5)', backdropFilter: 'blur(8px)', borderRadius: '16px', padding: '24px', border: '1px solid rgba(51,65,85,0.5)', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'white', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Cloud size={20} color="#60a5fa" />
              Infrastructure
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '16px' }}>
              {infrastructureOptions.map(infra => (
                <button
                  key={infra.id}
                  onClick={() => { setInfrastructure(infra.id); setRegion(infra.regions[0]); }}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: `2px solid ${infrastructure === infra.id ? '#3b82f6' : '#475569'}`,
                    background: infrastructure === infra.id ? 'rgba(59,130,246,0.1)' : 'rgba(51,65,85,0.3)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>{infra.icon}</span>
                  <div style={{ color: 'white', fontWeight: '500', marginTop: '8px' }}>{infra.name}</div>
                </button>
              ))}
            </div>
            <div>
              <label style={{ fontSize: '14px', color: '#9ca3af', display: 'block', marginBottom: '8px' }}>Region</label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                style={{ width: '100%', background: '#334155', border: '1px solid #475569', borderRadius: '8px', padding: '12px 16px', color: 'white', fontSize: '14px' }}
              >
                {infrastructureOptions.find(i => i.id === infrastructure)?.regions.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>
          
          <button
            onClick={() => setView('builder')}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '12px',
              background: project.color,
              color: 'white',
              fontWeight: '600',
              fontSize: '16px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Launch CX Builder →
          </button>
        </div>
      </div>
    );
  }

  // BUILDER VIEW
  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ padding: '6px', borderRadius: '8px', background: `${project.color}30` }}>
              <Layers size={20} color={project.color} />
            </div>
            <span style={{ fontWeight: 'bold', color: 'white' }}>CX Builder</span>
          </div>
          
          <div style={{ height: '24px', width: '1px', background: '#334155' }} />
          
          {/* Project Switcher */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowProjectSwitcher(!showProjectSwitcher)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '8px', background: `${project.color}20`, border: `1px solid ${project.color}40`, cursor: 'pointer' }}
            >
              <span style={{ fontSize: '20px' }}>{project.icon}</span>
              <span style={{ fontWeight: '600', color: 'white' }}>{project.name}</span>
              <ChevronsUpDown size={16} color="#9ca3af" />
            </button>
            
            {showProjectSwitcher && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '8px', width: '256px', background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', zIndex: 50, overflow: 'hidden' }}>
                <div style={{ padding: '8px', borderBottom: '1px solid #334155' }}>
                  <span style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0 8px' }}>Switch Project</span>
                </div>
                {Object.values(projects).map(p => (
                  <button
                    key={p.id}
                    onClick={() => {
                      setCurrentProject(p.id);
                      setShowProjectSwitcher(false);
                      setSelectedScreen(null);
                      setExpandedCategories(p.id === 'selfcare' ? ['onboarding', 'home'] : ['onboarding', 'wallet', 'transfers']);
                    }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: currentProject === p.id ? 'rgba(51,65,85,0.3)' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s' }}
                  >
                    <span style={{ fontSize: '24px' }}>{p.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: 'white' }}>{p.name}</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>{p.description}</div>
                    </div>
                    {currentProject === p.id && <Check size={16} color="#34d399" />}
                  </button>
                ))}
                <div style={{ padding: '8px', borderTop: '1px solid #334155' }}>
                  <button style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontSize: '14px', color: '#9ca3af', background: 'none', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                    <Plus size={14} /> Create New Project
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <select style={{ background: 'rgba(51,65,85,0.5)', border: '1px solid rgba(71,85,105,0.5)', borderRadius: '8px', padding: '6px 12px', fontSize: '14px', color: 'white' }}>
            <option>🇺🇸 English</option>
            <option>🇸🇦 Arabic</option>
            <option>🇫🇷 French</option>
            <option>🇮🇳 Hindi</option>
          </select>
          
          <button style={{ padding: '8px', borderRadius: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <Bell size={18} color="#9ca3af" />
          </button>
          
          <button style={{ padding: '8px', borderRadius: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <Settings size={18} color="#9ca3af" />
          </button>
          
          <button onClick={() => setShowPublishModal(true)} style={styles.button(project.color)}>
            <Play size={16} /> Publish
          </button>
          
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: '500' }}>
            PM
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <div style={styles.main}>
        {/* Left Panel */}
        <div style={styles.leftPanel}>
          <div style={styles.tabBar}>
            {[
              { id: 'screens', icon: Layout, label: 'Screens' },
              { id: 'elements', icon: Box, label: 'Elements' },
              { id: 'connectors', icon: Plug, label: 'Connectors' },
              { id: 'sdks', icon: Database, label: 'SDKs' },
            ].map(tab => (
              <button key={tab.id} onClick={() => setLeftTab(tab.id)} style={styles.tab(leftTab === tab.id, project.color)}>
                <tab.icon size={16} style={{ margin: '0 auto 4px' }} />
                {tab.label}
              </button>
            ))}
          </div>
          
          <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
            {leftTab === 'screens' && (
              <div>
                {Object.entries(screens).map(([category, screenList]) => (
                  <div key={category} style={{ marginBottom: '4px' }}>
                    <button
                      onClick={() => toggleCategory(category)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: '8px', background: 'none', border: 'none', cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {expandedCategories.includes(category) ? <ChevronDown size={14} color="#64748b" /> : <ChevronRight size={14} color="#64748b" />}
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db' }}>{categoryLabels[category]}</span>
                      </span>
                      <span style={{ fontSize: '12px', color: '#64748b', background: 'rgba(51,65,85,0.5)', padding: '2px 8px', borderRadius: '4px' }}>{screenList.length}</span>
                    </button>
                    
                    {expandedCategories.includes(category) && (
                      <div style={{ marginLeft: '16px' }}>
                        {screenList.map(screen => (
                          <button
                            key={screen.id}
                            onClick={() => setSelectedScreen(screen)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              textAlign: 'left',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              background: selectedScreen?.id === screen.id ? `${project.color}20` : 'transparent',
                              borderLeft: selectedScreen?.id === screen.id ? `2px solid ${project.color}` : '2px solid transparent',
                              color: selectedScreen?.id === screen.id ? 'white' : '#9ca3af',
                              border: 'none'
                            }}
                          >
                            <span>{screen.icon}</span>
                            <span style={{ fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{screen.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {leftTab === 'elements' && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', padding: '0 8px' }}>Colors</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    {designTokens.colors.map(color => (
                      <div key={color.name} style={{ cursor: 'pointer' }}>
                        <div style={{ width: '100%', aspectRatio: '1', borderRadius: '8px', border: '1px solid rgba(71,85,105,0.5)', background: color.value, marginBottom: '4px', transition: 'transform 0.2s' }} />
                        <span style={{ fontSize: '10px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{color.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', padding: '0 8px' }}>Components</h3>
                  {designTokens.components.map(comp => (
                    <div
                      key={comp.name}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', borderRadius: '8px', cursor: 'move', transition: 'all 0.2s' }}
                      draggable
                    >
                      <comp.icon size={16} color={project.color} />
                      <span style={{ fontSize: '14px', color: '#d1d5db' }}>{comp.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {leftTab === 'connectors' && (
              <div>
                {dataConnectors.map(connector => (
                  <div key={connector.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: '8px', transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '18px' }}>{connector.icon}</span>
                      <div>
                        <div style={{ fontSize: '14px', color: '#d1d5db' }}>{connector.name}</div>
                        {connector.version && <div style={{ fontSize: '12px', color: '#64748b' }}>{connector.version}</div>}
                      </div>
                    </div>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: connector.status === 'connected' ? '#34d399' : '#64748b' }} />
                  </div>
                ))}
              </div>
            )}
            
            {leftTab === 'sdks' && (
              <div>
                {sdkIntegrations.map(sdk => (
                  <div key={sdk.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: '8px', transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '18px' }}>{sdk.icon}</span>
                      <div>
                        <div style={{ fontSize: '14px', color: '#d1d5db' }}>{sdk.name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{sdk.events || sdk.crashes || sdk.delivered || sdk.blocked || sdk.calls || sdk.metrics || sdk.auth || sdk.verified}</div>
                      </div>
                    </div>
                    <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '12px', background: sdk.status === 'connected' ? 'rgba(34,197,94,0.2)' : 'rgba(100,116,139,0.2)', color: sdk.status === 'connected' ? '#34d399' : '#9ca3af' }}>
                      {sdk.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Center Panel */}
        <div style={styles.centerPanel}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', borderBottom: '1px solid rgba(51,65,85,0.5)' }}>
            {[
              { id: 'mobile', icon: Smartphone, label: 'Mobile', width: 375 },
              { id: 'tablet', icon: Tablet, label: 'Tablet', width: 768 },
              { id: 'desktop', icon: Monitor, label: 'Desktop', width: 1200 },
            ].map(device => (
              <button
                key={device.id}
                onClick={() => setDevicePreview(device.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  background: devicePreview === device.id ? `${project.color}20` : 'transparent',
                  border: 'none',
                  color: devicePreview === device.id ? 'white' : '#64748b',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <device.icon size={16} />
                <span style={{ fontSize: '14px' }}>{device.label}</span>
              </button>
            ))}
          </div>
          
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', overflow: 'auto' }}>
            <div
              style={{
                background: '#020617',
                borderRadius: '32px',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                overflow: 'hidden',
                transition: 'all 0.3s',
                width: devicePreview === 'mobile' ? '375px' : devicePreview === 'tablet' ? '768px' : '100%',
                maxWidth: devicePreview === 'desktop' ? '1200px' : undefined,
                height: devicePreview === 'mobile' ? '750px' : devicePreview === 'tablet' ? '550px' : '600px',
              }}
            >
              {devicePreview === 'mobile' && (
                <div style={{ height: '32px', background: '#020617', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: '128px', height: '24px', background: 'black', borderRadius: '0 0 16px 16px' }} />
                </div>
              )}
              
              <div style={{ height: devicePreview === 'mobile' ? 'calc(100% - 32px)' : '100%', background: '#0f172a', overflow: 'auto' }}>
                {selectedScreen ? (
                  <ScreenPreview screen={selectedScreen} project={currentProject} projectColor={project.color} />
                ) : (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                    <Layout size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                    <p>Select a screen to preview</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {devicePreview === 'mobile' && selectedScreen && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '4px', background: '#1e293b', borderRadius: '9999px', padding: '4px' }}>
                {currentProject === 'selfcare' ? (
                  <>
                    <button style={{ padding: '12px', borderRadius: '9999px', background: 'none', border: 'none', color: project.color, cursor: 'pointer' }}><Home size={20} /></button>
                    <button style={{ padding: '12px', borderRadius: '9999px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><Package size={20} /></button>
                    <button style={{ padding: '12px', borderRadius: '9999px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><Gamepad2 size={20} /></button>
                    <button style={{ padding: '12px', borderRadius: '9999px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><Crown size={20} /></button>
                    <button style={{ padding: '12px', borderRadius: '9999px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><Menu size={20} /></button>
                  </>
                ) : (
                  <>
                    <button style={{ padding: '12px', borderRadius: '9999px', background: 'none', border: 'none', color: project.color, cursor: 'pointer' }}><Home size={20} /></button>
                    <button style={{ padding: '12px', borderRadius: '9999px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><Send size={20} /></button>
                    <button style={{ padding: '12px', borderRadius: '9999px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><QrCode size={20} /></button>
                    <button style={{ padding: '12px', borderRadius: '9999px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><Receipt size={20} /></button>
                    <button style={{ padding: '12px', borderRadius: '9999px', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><Menu size={20} /></button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Right Panel */}
        <div style={styles.rightPanel}>
          <div style={styles.tabBar}>
            {[
              { id: 'ai', icon: Sparkles, label: 'AI Build' },
              { id: 'manual', icon: Edit3, label: 'Manual' },
              { id: 'cms', icon: FileText, label: 'CMS' },
              { id: 'analytics', icon: BarChart3, label: 'Analytics' },
            ].map(tab => (
              <button key={tab.id} onClick={() => setRightTab(tab.id)} style={styles.tab(rightTab === tab.id, project.color)}>
                <tab.icon size={16} style={{ margin: '0 auto 4px' }} />
                {tab.label}
              </button>
            ))}
          </div>
          
          <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
            {rightTab === 'ai' && (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db', display: 'block', marginBottom: '8px' }}>Describe what you want to build</label>
                  <textarea
                    style={{ width: '100%', height: '96px', background: 'rgba(51,65,85,0.5)', border: '1px solid rgba(71,85,105,0.5)', borderRadius: '12px', padding: '12px 16px', color: 'white', resize: 'none', fontSize: '14px' }}
                    placeholder={currentProject === 'selfcare' ? "E.g., Add a data usage chart with breakdown by app..." : "E.g., Create a send money flow with contact picker..."}
                  />
                  <button style={{ width: '100%', marginTop: '8px', padding: '10px', borderRadius: '8px', background: project.color, color: 'white', fontWeight: '500', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <Sparkles size={16} /> Generate with AI
                  </button>
                </div>
                
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#9ca3af', marginBottom: '12px' }}>Quick Prompts</h3>
                  {aiPrompts.map((prompt, i) => (
                    <button
                      key={i}
                      style={{ width: '100%', textAlign: 'left', padding: '8px 12px', borderRadius: '8px', background: 'rgba(51,65,85,0.3)', border: 'none', fontSize: '14px', color: '#d1d5db', marginBottom: '8px', cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {rightTab === 'cms' && (
              <div>
                <div style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '8px' }}>Business users can manage:</div>
                {contentTypes.map(content => (
                  <div
                    key={content.id}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', borderRadius: '8px', background: 'rgba(51,65,85,0.3)', marginBottom: '8px', cursor: 'pointer', transition: 'all 0.2s' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '20px' }}>{content.icon}</span>
                      <div>
                        <div style={{ fontSize: '14px', color: 'white' }}>{content.name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{content.count} items</div>
                      </div>
                    </div>
                    <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '12px', background: content.status === 'active' ? 'rgba(34,197,94,0.2)' : 'rgba(234,179,8,0.2)', color: content.status === 'active' ? '#34d399' : '#fbbf24' }}>
                      {content.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {rightTab === 'analytics' && (
              <div>
                <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db', marginBottom: '12px' }}>Digital KPIs</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
                  {[
                    { label: 'DAU', value: currentProject === 'selfcare' ? '125K' : '450K', change: '+12%' },
                    { label: 'MAU', value: currentProject === 'selfcare' ? '1.2M' : '3.8M', change: '+8%' },
                    { label: 'Retention', value: '68%', change: '+5%' },
                    { label: 'Conversion', value: currentProject === 'selfcare' ? '23%' : '34%', change: '+3%' },
                  ].map(kpi => (
                    <div key={kpi.label} style={{ padding: '12px', borderRadius: '8px', background: 'rgba(51,65,85,0.3)' }}>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{kpi.label}</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'white' }}>{kpi.value}</div>
                      <div style={{ fontSize: '12px', color: '#34d399' }}>{kpi.change}</div>
                    </div>
                  ))}
                </div>
                
                {currentProject === 'mobilemoney' && (
                  <div style={{ paddingTop: '16px', borderTop: '1px solid #334155' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '500', color: '#d1d5db', marginBottom: '8px' }}>Transaction Metrics</h4>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px' }}>
                      <span style={{ color: '#9ca3af' }}>Daily Volume</span>
                      <span style={{ color: 'white' }}>$2.4M</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px' }}>
                      <span style={{ color: '#9ca3af' }}>Transactions</span>
                      <span style={{ color: 'white' }}>145K</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                      <span style={{ color: '#9ca3af' }}>Success Rate</span>
                      <span style={{ color: '#34d399' }}>99.7%</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Publish Modal */}
      {showPublishModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#1e293b', borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '100%', margin: '16px', textAlign: 'center' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: `${project.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Check size={32} color={project.color} />
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: 'white', margin: '0 0 8px' }}>Published!</h2>
            <p style={{ color: '#9ca3af', marginBottom: '24px' }}>Your {project.fullName} is now live</p>
            <div style={{ marginBottom: '24px' }}>
              <a href="#" style={{ display: 'block', color: '#60a5fa', marginBottom: '12px', textDecoration: 'none' }}>📱 iOS App Store →</a>
              <a href="#" style={{ display: 'block', color: '#34d399', textDecoration: 'none' }}>🤖 Google Play Store →</a>
            </div>
            <button
              onClick={() => setShowPublishModal(false)}
              style={{ width: '100%', padding: '12px', borderRadius: '12px', background: project.color, color: 'white', fontWeight: '500', border: 'none', cursor: 'pointer' }}
            >
              Continue Building
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CXBuilderMultiProject;
